import React, { useEffect, useMemo, useRef, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  providerId: '',
  providerName: '',
  credentials: '',
  licenseNumber: '',
  licenseState: '',
  signatureImageReference: '',
  signaturePrefixText: '',
  requiredSignatureText: '',
  active: true,
  displayOrder: 0,
  adminNotes: '',
};

function normalizeSignatureToForm(signature) {
  return {
    providerId:
      signature?.providerId === null || signature?.providerId === undefined
        ? ''
        : String(signature.providerId),
    providerName: signature?.providerName || '',
    credentials: signature?.credentials || '',
    licenseNumber: signature?.licenseNumber || '',
    licenseState: signature?.licenseState || '',
    signatureImageReference: signature?.signatureImageReference || '',
    signaturePrefixText: signature?.signaturePrefixText || '',
    requiredSignatureText: signature?.requiredSignatureText || '',
    active: Boolean(signature?.active),
    displayOrder:
      signature?.displayOrder === null || signature?.displayOrder === undefined
        ? 0
        : Number(signature.displayOrder),
    adminNotes: signature?.adminNotes || '',
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function validateRequiredPositiveInteger(value, message) {
  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue <= 0) {
    return message;
  }

  return '';
}

function validateRequiredNonNegativeInteger(value, message) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return message;
  }

  const numericValue = Number(value);

  if (!Number.isInteger(numericValue) || numericValue < 0) {
    return message;
  }

  return '';
}

function validateForm(form) {
  const errors = {};
  const providerName = String(form.providerName || '').trim();
  const credentials = String(form.credentials || '').trim();
  const licenseNumber = String(form.licenseNumber || '').trim();
  const licenseState = String(form.licenseState || '').trim();
  const signatureImageReference = String(
    form.signatureImageReference || ''
  ).trim();
  const signaturePrefixText = String(form.signaturePrefixText || '').trim();
  const requiredSignatureText = String(form.requiredSignatureText || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  const providerIdError = validateRequiredPositiveInteger(
    form.providerId,
    'Provider ID must be a positive whole number.'
  );

  if (providerIdError) {
    errors.providerId = providerIdError;
  }

  if (!providerName) {
    errors.providerName = 'Provider name is required.';
  } else if (providerName.length > 200) {
    errors.providerName = 'Provider name cannot exceed 200 characters.';
  }

  if (!credentials) {
    errors.credentials = 'Provider credentials are required.';
  } else if (credentials.length > 100) {
    errors.credentials = 'Provider credentials cannot exceed 100 characters.';
  }

  if (!licenseNumber) {
    errors.licenseNumber = 'License number is required.';
  } else if (licenseNumber.length > 100) {
    errors.licenseNumber = 'License number cannot exceed 100 characters.';
  }

  if (licenseState.length > 50) {
    errors.licenseState = 'License state cannot exceed 50 characters.';
  }

  if (!signatureImageReference) {
    errors.signatureImageReference = 'A signature image must be uploaded.';
  } else if (signatureImageReference.length > 1000) {
    errors.signatureImageReference =
      'Signature image reference cannot exceed 1000 characters.';
  }

  if (signaturePrefixText.length > 250) {
    errors.signaturePrefixText =
      'Signature prefix text cannot exceed 250 characters.';
  }

  if (requiredSignatureText.length > 1000) {
    errors.requiredSignatureText =
      'Required signature text cannot exceed 1000 characters.';
  }

  const displayOrderError = validateRequiredNonNegativeInteger(
    form.displayOrder,
    'Display order must be a whole number of 0 or greater.'
  );

  if (displayOrderError) {
    errors.displayOrder = displayOrderError;
  }

  if (adminNotes.length > 500) {
    errors.adminNotes = 'Admin notes cannot exceed 500 characters.';
  }

  return errors;
}

function buildRequest(form) {
  const licenseState = String(form.licenseState || '').trim();
  const signaturePrefixText = String(form.signaturePrefixText || '').trim();
  const requiredSignatureText = String(form.requiredSignatureText || '').trim();
  const adminNotes = String(form.adminNotes || '').trim();

  return {
    providerId: Number(form.providerId),
    providerName: String(form.providerName || '').trim(),
    credentials: String(form.credentials || '').trim(),
    licenseNumber: String(form.licenseNumber || '').trim(),
    licenseState: licenseState || null,
    signatureImageReference: String(form.signatureImageReference || '').trim(),
    signaturePrefixText: signaturePrefixText || null,
    requiredSignatureText: requiredSignatureText || null,
    active: Boolean(form.active),
    displayOrder: Number(form.displayOrder),
    adminNotes: adminNotes || null,
  };
}

function licenseLabel(signature) {
  const parts = [signature?.licenseState, signature?.licenseNumber].filter(
    Boolean
  );

  return parts.length > 0 ? parts.join(' ') : 'N/A';
}

function SignatureImagePreview({ providerSignatureId, localPreviewUrl }) {
  const [securePreviewUrl, setSecurePreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    async function loadSecurePreview() {
      if (!providerSignatureId || localPreviewUrl) {
        setSecurePreviewUrl('');
        setError('');
        return;
      }

      try {
        setLoading(true);
        setError('');

        const blob =
          await clinicalTemplateAdminService.getProviderSignatureImageBlob(
            providerSignatureId
          );

        if (!active) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setSecurePreviewUrl(objectUrl);
      } catch (requestError) {
        if (active) {
          setSecurePreviewUrl('');
          setError(
            requestError?.message || 'Signature image preview could not be loaded.'
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadSecurePreview();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [providerSignatureId, localPreviewUrl]);

  const previewUrl = localPreviewUrl || securePreviewUrl;

  return (
    <div
      style={{
        border: '1px solid #d8dde5',
        borderRadius: 10,
        minHeight: 135,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 14,
        background: '#fff',
      }}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="Provider signature preview"
          style={{
            maxWidth: '100%',
            maxHeight: 115,
            objectFit: 'contain',
          }}
        />
      ) : loading ? (
        <span>Loading secure signature preview...</span>
      ) : error ? (
        <span className="clinical-template-field-error">{error}</span>
      ) : (
        <span style={{ color: '#6b7280' }}>No signature image preview yet.</span>
      )}
    </div>
  );
}

export default function ProviderSignatureAdminSection() {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingSignature, setEditingSignature] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [providerFilter, setProviderFilter] = useState('ALL');
  const fileInputRef = useRef(null);

  const isEditing = Boolean(editingSignature?.providerSignatureId);

  const providerOptions = useMemo(() => {
    const byId = new Map();

    signatures.forEach((signature) => {
      if (signature?.providerId === null || signature?.providerId === undefined) {
        return;
      }

      const key = String(signature.providerId);
      const current = byId.get(key);
      const candidate = {
        providerId: signature.providerId,
        providerName: signature.providerName || `Provider #${signature.providerId}`,
      };

      if (!current || candidate.providerName.localeCompare(current.providerName) < 0) {
        byId.set(key, candidate);
      }
    });

    return [...byId.values()].sort((first, second) =>
      String(first.providerName || '').localeCompare(
        String(second.providerName || '')
      )
    );
  }, [signatures]);

  const filteredSignatures = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...signatures]
      .filter((signature) => {
        if (statusFilter === 'ACTIVE' && !signature.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && signature.active) {
          return false;
        }

        if (
          providerFilter !== 'ALL' &&
          String(signature.providerId) !== providerFilter
        ) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          signature.providerSignatureId,
          signature.providerId,
          signature.providerName,
          signature.credentials,
          signature.licenseNumber,
          signature.licenseState,
          signature.signaturePrefixText,
          signature.requiredSignatureText,
          signature.adminNotes,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.providerName || '').localeCompare(
          String(second.providerName || '')
        );
      });
  }, [signatures, searchTerm, statusFilter, providerFilter]);

  const activeCount = useMemo(
    () => signatures.filter((signature) => signature.active).length,
    [signatures]
  );

  const providerCount = useMemo(
    () =>
      new Set(
        signatures
          .map((signature) => signature.providerId)
          .filter((value) => value !== null && value !== undefined)
      ).size,
    [signatures]
  );

  useEffect(() => {
    loadConfiguration();
  }, []);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const data = await clinicalTemplateAdminService.getAllProviderSignatures();
      setSignatures(Array.isArray(data) ? data : []);
    } catch (error) {
      setSignatures([]);
      setLoadError(
        error?.message || 'Provider-signature configuration could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  const clearLocalPreview = () => {
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
    }
    setLocalPreviewUrl('');
  };

  const resetForm = () => {
    clearLocalPreview();
    setEditingSignature(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setSelectedFile(null);
    setActionError('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const beginEdit = (signature) => {
    clearLocalPreview();
    setEditingSignature(signature);
    setForm(normalizeSignatureToForm(signature));
    setFormErrors({});
    setSelectedFile(null);
    setActionError('');

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;

    clearLocalPreview();
    setSelectedFile(file);
    setActionError('');

    if (!file) {
      return;
    }

    if (file.type && !file.type.startsWith('image/')) {
      setSelectedFile(null);
      setActionError('The selected signature file must be an image.');
      event.target.value = '';
      return;
    }

    setLocalPreviewUrl(URL.createObjectURL(file));
  };

  const uploadSignatureImage = async () => {
    const providerIdError = validateRequiredPositiveInteger(
      form.providerId,
      'Enter a valid provider ID before uploading a signature image.'
    );

    if (providerIdError) {
      setFormErrors((current) => ({
        ...current,
        providerId: providerIdError,
      }));
      setActionError(providerIdError);
      return;
    }

    if (!selectedFile) {
      setActionError('Choose a signature image before uploading.');
      return;
    }

    try {
      setUploading(true);
      setActionError('');

      const result =
        await clinicalTemplateAdminService.uploadProviderSignatureImage(
          Number(form.providerId),
          selectedFile
        );

      updateFormField(
        'signatureImageReference',
        result.signatureImageReference
      );
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      showSuccess(
        'Signature image uploaded. Save the provider-signature record to keep this reference.'
      );
    } catch (error) {
      setActionError(
        error?.message || 'Provider signature image could not be uploaded.'
      );
    } finally {
      setUploading(false);
    }
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updateProviderSignature(
          editingSignature.providerSignatureId,
          request
        );
        showSuccess('Provider signature updated.');
      } else {
        await clinicalTemplateAdminService.createProviderSignature(request);
        showSuccess('Provider signature created.');
      }

      resetForm();
      await loadConfiguration();
    } catch (error) {
      setActionError(
        error?.message || 'Provider signature could not be saved.'
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (signature) => {
    const nextActive = !signature.active;

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate the signature for "${signature.providerName}" (${licenseLabel(
          signature
        )})? Historical signed notes are preserved by their permanent signature snapshots.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(signature.providerSignatureId);
      setActionError('');

      await clinicalTemplateAdminService.setProviderSignatureActive(
        signature.providerSignatureId,
        nextActive
      );

      showSuccess(
        `Provider signature ${nextActive ? 'activated' : 'deactivated'}.`
      );
      await loadConfiguration();
    } catch (error) {
      setActionError(
        error?.message || 'Provider signature status could not be updated.'
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="clinical-template-section-layout">
      {success && (
        <div className="clinical-template-message success" role="status">
          {success}
        </div>
      )}

      {actionError && (
        <div className="clinical-template-message error" role="alert">
          {actionError}
        </div>
      )}

      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading">
          <span className="clinical-template-eyebrow">
            PROVIDER SIGNATURE CONFIGURATION
          </span>
          <h2>
            {isEditing ? 'Edit Provider Signature' : 'Create Provider Signature'}
          </h2>
          <p>
            Configure the reusable signature record used by clinical-note
            signing. Upload the private signature image first, then save the
            signature record with the returned image reference.
          </p>
        </div>

        {isEditing && (
          <div className="clinical-template-next-step-box">
            <strong>Provider ownership is permanent</strong>
            <p>
              Provider #{editingSignature.providerId} cannot be replaced on
              this signature record. Create a new signature record if a
              different provider is required.
            </p>
          </div>
        )}

        <form onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Provider ID
              <input
                type="number"
                min="1"
                step="1"
                value={form.providerId}
                onChange={(event) =>
                  updateFormField('providerId', event.target.value)
                }
                disabled={saving || uploading || isEditing}
                placeholder="Existing doctor/provider ID"
              />
              <small>
                Must reference an existing provider. This value becomes
                immutable after creation.
              </small>
              {formErrors.providerId && (
                <span className="clinical-template-field-error">
                  {formErrors.providerId}
                </span>
              )}
            </label>

            <label>
              Provider Name
              <input
                type="text"
                value={form.providerName}
                onChange={(event) =>
                  updateFormField('providerName', event.target.value)
                }
                maxLength={200}
                disabled={saving || uploading}
                placeholder="Name exactly as it should appear on the note"
              />
              {formErrors.providerName && (
                <span className="clinical-template-field-error">
                  {formErrors.providerName}
                </span>
              )}
            </label>

            <label>
              Credentials
              <input
                type="text"
                value={form.credentials}
                onChange={(event) =>
                  updateFormField('credentials', event.target.value)
                }
                maxLength={100}
                disabled={saving || uploading}
                placeholder="PT, DPT"
              />
              {formErrors.credentials && (
                <span className="clinical-template-field-error">
                  {formErrors.credentials}
                </span>
              )}
            </label>

            <label>
              License Number
              <input
                type="text"
                value={form.licenseNumber}
                onChange={(event) =>
                  updateFormField('licenseNumber', event.target.value)
                }
                maxLength={100}
                disabled={saving || uploading}
              />
              {formErrors.licenseNumber && (
                <span className="clinical-template-field-error">
                  {formErrors.licenseNumber}
                </span>
              )}
            </label>

            <label>
              License State / Jurisdiction
              <input
                type="text"
                value={form.licenseState}
                onChange={(event) =>
                  updateFormField('licenseState', event.target.value)
                }
                maxLength={50}
                disabled={saving || uploading}
                placeholder="NY"
              />
              {formErrors.licenseState && (
                <span className="clinical-template-field-error">
                  {formErrors.licenseState}
                </span>
              )}
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving || uploading}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(260px, 0.8fr)',
              gap: 16,
              marginTop: 18,
            }}
          >
            <div>
              <label className="clinical-template-full-width-field">
                Private Signature Image
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={saving || uploading}
                />
                <small>
                  The image is uploaded through the secured provider-signature
                  image endpoint and is never loaded from a public image path.
                </small>
              </label>

              <div className="clinical-template-form-actions">
                <button
                  type="button"
                  onClick={uploadSignatureImage}
                  disabled={saving || uploading || !selectedFile}
                >
                  {uploading ? 'Uploading Image...' : 'Upload Signature Image'}
                </button>
              </div>

              {form.signatureImageReference ? (
                <div className="clinical-template-next-step-box">
                  <strong>Private image reference ready</strong>
                  <p>
                    The image upload succeeded. Save this signature record to
                    persist the reference with the provider credentials.
                  </p>
                </div>
              ) : null}

              {formErrors.signatureImageReference && (
                <span className="clinical-template-field-error">
                  {formErrors.signatureImageReference}
                </span>
              )}
            </div>

            <SignatureImagePreview
              providerSignatureId={editingSignature?.providerSignatureId}
              localPreviewUrl={localPreviewUrl}
            />
          </div>

          <div className="clinical-template-form-grid" style={{ marginTop: 18 }}>
            <label>
              Signature Prefix Text
              <input
                type="text"
                value={form.signaturePrefixText}
                onChange={(event) =>
                  updateFormField('signaturePrefixText', event.target.value)
                }
                maxLength={250}
                disabled={saving || uploading}
                placeholder="Electronically Signed by"
              />
              {formErrors.signaturePrefixText && (
                <span className="clinical-template-field-error">
                  {formErrors.signaturePrefixText}
                </span>
              )}
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) =>
                  updateFormField('active', event.target.checked)
                }
                disabled={saving || uploading}
              />
              <span>
                <strong>Active</strong>
                <small>
                  Active signatures may be selected during clinical-note
                  signing.
                </small>
              </span>
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Required Signature Text
            <textarea
              rows={4}
              value={form.requiredSignatureText}
              onChange={(event) =>
                updateFormField('requiredSignatureText', event.target.value)
              }
              maxLength={1000}
              disabled={saving || uploading}
              placeholder="Optional required wording beneath or within the signature section..."
            />
            <small>
              {String(form.requiredSignatureText || '').length}/1000 characters
            </small>
            {formErrors.requiredSignatureText && (
              <span className="clinical-template-field-error">
                {formErrors.requiredSignatureText}
              </span>
            )}
          </label>

          <label className="clinical-template-full-width-field">
            Admin Notes
            <textarea
              rows={4}
              value={form.adminNotes}
              onChange={(event) =>
                updateFormField('adminNotes', event.target.value)
              }
              maxLength={500}
              disabled={saving || uploading}
              placeholder="Optional Admin/Manager-only notes..."
            />
            <small>{String(form.adminNotes || '').length}/500 characters</small>
            {formErrors.adminNotes && (
              <span className="clinical-template-field-error">
                {formErrors.adminNotes}
              </span>
            )}
          </label>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving || uploading}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Update Provider Signature'
                  : 'Create Provider Signature'}
            </button>

            {isEditing && (
              <button
                type="button"
                className="clinical-template-secondary-button"
                onClick={resetForm}
                disabled={saving || uploading}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="clinical-template-list-panel clinical-template-editor-panel">
        <div className="clinical-template-editor-heading-row">
          <div className="clinical-template-editor-heading">
            <span className="clinical-template-eyebrow">
              CONFIGURED SIGNATURES
            </span>
            <h2>Provider Signatures</h2>
            <p>
              Inactive records remain available for historical references.
              Signed notes preserve their own permanent signature snapshot.
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={loadConfiguration}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="clinical-template-card-grid" style={{ marginTop: 16 }}>
          <div className="clinical-template-card" style={{ cursor: 'default' }}>
            <span>Total Signatures</span>
            <strong>{signatures.length}</strong>
          </div>
          <div className="clinical-template-card" style={{ cursor: 'default' }}>
            <span>Active Signatures</span>
            <strong>{activeCount}</strong>
          </div>
          <div className="clinical-template-card" style={{ cursor: 'default' }}>
            <span>Configured Providers</span>
            <strong>{providerCount}</strong>
          </div>
        </div>

        <div className="clinical-template-list-toolbar"
          style={{ gridTemplateColumns: 'minmax(220px, 1fr) 180px 260px' }}>
          <label>
            Search
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Name, license, credentials, provider ID..."
            />
          </label>

          <label>
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </label>

          <label>
            Provider
            <select
              value={providerFilter}
              onChange={(event) => setProviderFilter(event.target.value)}
            >
              <option value="ALL">All Providers</option>
              {providerOptions.map((provider) => (
                <option
                  key={provider.providerId}
                  value={String(provider.providerId)}
                >
                  {provider.providerName} (#{provider.providerId})
                </option>
              ))}
            </select>
          </label>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        {!loading && !loadError && filteredSignatures.length === 0 && (
          <div className="clinical-template-empty-state">
            No provider signatures match the current filters.
          </div>
        )}

        {filteredSignatures.length > 0 && (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Provider</th>
                  <th>Credentials / License</th>
                  <th>Signature Wording</th>
                  <th>Status</th>
                  <th>Audit</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSignatures.map((signature) => (
                  <tr key={signature.providerSignatureId}>
                    <td>{signature.displayOrder}</td>
                    <td>
                      <strong>{signature.providerName}</strong>
                      <small>
                        Provider #{signature.providerId} · Signature #
                        {signature.providerSignatureId}
                      </small>
                    </td>
                    <td>
                      <div>{signature.credentials}</div>
                      <small>
                        {licenseLabel(signature)}
                      </small>
                    </td>
                    <td>
                      <div>
                        {signature.signaturePrefixText || 'No prefix text'}
                      </div>
                      <small>
                        {signature.requiredSignatureText ||
                          'No additional required text'}
                      </small>
                    </td>
                    <td>
                      <strong>{signature.active ? 'Active' : 'Inactive'}</strong>
                    </td>
                    <td>
                      <small>
                        Created: {formatDateTime(signature.createdAt)}
                      </small>
                      <small>
                        Updated: {formatDateTime(signature.updatedAt)}
                      </small>
                      <small>
                        Staff: {signature.updatedByStaffId ?? signature.createdByStaffId ?? 'N/A'}
                      </small>
                    </td>
                    <td>
                      <div className="clinical-template-row-actions">
                        <button
                          type="button"
                          className="clinical-template-secondary-button"
                          onClick={() => beginEdit(signature)}
                          disabled={
                            saving ||
                            uploading ||
                            activeActionId === signature.providerSignatureId
                          }
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className="clinical-template-secondary-button"
                          onClick={() => toggleActive(signature)}
                          disabled={
                            saving ||
                            uploading ||
                            activeActionId === signature.providerSignatureId
                          }
                        >
                          {activeActionId === signature.providerSignatureId
                            ? 'Updating...'
                            : signature.active
                              ? 'Deactivate'
                              : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
